import PropTypes from 'prop-types'
import React, { Component } from 'react'
import AsyncSelect from 'react-select/lib/Async'
import Browser from '../../services/Browser'
import HigherOrderField from './higher-order-field.component'
import { selectStyles } from './styles/select.style'
import { StorageService } from '../../services/storage.service'
import { isMobile } from './../../helpers/window.helper'
import scrollIntoView from 'scroll-into-view-if-needed'

class BaseAsyncSelectField extends Component {
	constructor(props) {
		super(props)
		const { elementRef } = this.props
		this.elementRef = elementRef || React.createRef()
		this.selectRef = React.createRef()
		this.state = { value: undefined, isLoading: false, inputValue: '' }
		this.lastOrigin = null
		this.lastDestination = null
	}

	handleChange = value => {
		this.setState({ value: value || null, isLoading: false }, () => {
			const { id, onChange, onValueChange } = this.props
			const newValue = !!value ? value.value : ''
			onChange(id, newValue)
			onValueChange && onValueChange(newValue)
		})
	}

	handleBlur = () => {
		const { id, onBlur } = this.props
		onBlur(id, true)
	}

	getOption = opt => {
		if (!opt) return Promise.resolve(null)

		const { inputValue } = this.state
		const { loadOptions } = this.props
		return new Promise(resolve => {
			loadOptions(inputValue, options => {
				resolve((options && options.find(o => o.value === opt)) || null)
			})
		})
	}

	componentDidMount() {
		const { value: formikValue, defaultValue } = this.props

		if (!formikValue && !defaultValue) return

		if (!formikValue && defaultValue) {
			this.setState({ value: defaultValue })
			return
		}

		if (!defaultValue || defaultValue.value !== formikValue) {
			this.getOption(formikValue).then(value => this.handleChange(value))
		}
	}

	shouldSyncValue(currentOption, formikValue, defaultValue) {
		if (currentOption === undefined) return true

		const currentValue = currentOption ? currentOption.value : ''

		if ((formikValue || '') !== currentValue) return true

		if (defaultValue && defaultValue.value !== currentValue) return true

		return false
	}

	componentDidUpdate(prevProps) {
		const { value: currentOption } = this.state
		const { value: formikValue, defaultValue, id } = this.props

		const prevFormikValue = prevProps.value
		const prevDefaultValue = prevProps.defaultValue

		// Only update if values have actually changed
		if (
			formikValue === prevFormikValue &&
			defaultValue === prevDefaultValue
		) {
			return
		}

		const hasNoExternalValue = !formikValue && !defaultValue

		if (hasNoExternalValue) {
			if (currentOption) {
				this.setState({ value: null, isLoading: false })
			}
			return
		}

		if (!this.shouldSyncValue(currentOption, formikValue, defaultValue))
			return

		if (defaultValue) {
			this.setState({ value: defaultValue })
			return
		}

		this.getOption(formikValue).then(value => this.handleChange(value))
		this.syncToStorage(currentOption, id)
	}

	scrollOrigin = position => {
		let originElem = document.getElementById('select-origin')
		originElem = originElem.parentElement

		scrollIntoView(originElem, {
			behavior: 'smooth',
			block: position,
			scrollMode: 'always'
		})
	}

	scrollDestination = position => {
		let destinationElem = document.getElementById('select-destination')

		scrollIntoView(destinationElem, {
			behavior: 'smooth',
			block: position,
			scrollMode: 'always'
		})
	}

	syncToStorage = (value, id) => {
		if (!value || !value.value) return

		if (id === 'origin') {
			StorageService.lastOrigin.set(value)
		} else if (id === 'destination') {
			StorageService.lastDestination.set(value)
		}
	}

	render() {
		const {
			id,
			icon,
			loadOptions,
			placeholder,
			defaultValue,
			defaultOptions,
			disabled
		} = this.props

		const { value, isLoading } = this.state

		const isIE = Browser.isIE()

		return (
			<HigherOrderField
				full
				asyncSelect
				ref={this.elementRef}
				innerRef={this.elementRef}
				{...this.props}>
				<AsyncSelect
					id={id}
					isDisabled={disabled}
					value={value || null}
					isLoading={isLoading}
					styles={selectStyles}
					onBlur={this.handleBlur}
					loadOptions={loadOptions}
					innerRef={this.selectRef}
					defaultValue={defaultValue}
					onMenuScrollToBottom={isIE}
					onChange={this.handleChange}
					isClearable={true}
					backspaceRemovesValue={true}
					menuShouldBlockScroll={isIE}
					className={icon ? 'with-icon' : ''}
					loadingMessage={() => 'Carregando ...'}
					menuPosition={isIE ? 'fixed' : 'absolute'}
					placeholder={placeholder || 'Selecione...'}
					defaultOptions={
						Array.isArray(defaultOptions) ? defaultOptions : []
					}
					onInputChange={(data, { action }) => {
						const isTyping = action === 'input-change'

						if (isTyping && this.state.value) {
							this.setState(
								{
									value: null,
									inputValue: data,
									isLoading: isTyping
								},
								() => {
									const { id, onChange, onValueChange } = this.props
									onChange(id, '')
									onValueChange && onValueChange('')
								}
							)
						} else {
							this.setState({ inputValue: data, isLoading: isTyping })
						}
					}}
					noOptionsMessage={() => (
						<div style={{ fontSize: '16px', cursor: 'default' }}>
							Nenhuma opção para pesquisa
						</div>
					)}
					onMenuOpen={() => {
						const selects = [
							...document.querySelectorAll('[id^="field-"]')
						]

						selects.forEach(elem => {
							if (elem.id !== `field-${id}`) {
								elem.style.zIndex = 0
							}
						})

						if (isMobile()) {
							if (id === 'origin') this.scrollOrigin('start')
							else if (id === 'destination')
								this.scrollDestination('start')
						}
					}}
					onMenuClose={() => {
						const selects = [
							...document.querySelectorAll('[id^="field-"]')
						]

						selects.forEach(elem => {
							elem.style.zIndex = 'auto'
						})

						if (isMobile()) {
							if (id === 'origin') this.scrollOrigin('center')
							else if (id === 'destination')
								this.scrollDestination('center')
						}
					}}
				/>
			</HigherOrderField>
		)
	}
}

BaseAsyncSelectField.propTypes = {
	id: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	onBlur: PropTypes.func.isRequired,
	loadOptions: PropTypes.func.isRequired,
	onValueChange: PropTypes.func,
	value: PropTypes.string,
	defaultValue: PropTypes.object,
	defaultOptions: PropTypes.array,
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
	elementRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any })
	]),
	icon: PropTypes.func
}

BaseAsyncSelectField.defaultProps = {
	onValueChange: null,
	value: '',
	defaultValue: null,
	defaultOptions: [],
	disabled: false,
	placeholder: 'Selecione...',
	elementRef: null,
	icon: null
}

export default BaseAsyncSelectField
